/**
 * Central Column Configuration Registry
 *
 * Single source of truth for all table column configurations across the platform.
 *
 * Organization:
 * - shared: Columns used by multiple entity types (email, tags, status, dates)
 * - seed: Seed-specific columns (registrar, registrant, country)
 * - asset: Asset-specific columns (attackSurface, origins, port, protocol)
 * - technology: Technology-specific columns (vendor, name)
 * - risk: Risk-specific columns (future)
 *
 * Usage:
 * ```typescript
 * import { getColumnWidth } from '@/config/columns/columnRegistry';
 *
 * const emailWidth = getColumnWidth('seed', 'email');
 * // { fixedWidth: 200, minWidth: 200, maxWidth: 200 }
 * ```
 *
 * File History:
 * - 2025-10-19: Initial creation - Day 1 of centralized column configuration migration
 *               Extracted all widths from Seeds, Assets, and Technologies table configs
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Column width configuration
 *
 * Width properties:
 * - fixedWidth: The actual rendered width in pixels (for fixed-width columns only)
 * - minWidth: Minimum width constraint (required for all columns)
 * - maxWidth: Maximum width constraint (omit for truly expandable columns)
 *
 * Column types:
 * - Fixed: { fixedWidth: 200, minWidth: 200, maxWidth: 200 }
 * - Expandable: { minWidth: 200 } (no fixedWidth or maxWidth)
 * - Flexible: { minWidth: 200, maxWidth: 400 } (no fixedWidth)
 */
export interface ColumnWidth {
  fixedWidth?: number;
  minWidth: number;
  maxWidth?: number;
}

/**
 * Table variant type
 *
 * Variants allow different column widths based on context:
 * - default: Full-page tables with ample horizontal space
 * - compact: Drawer/constrained contexts (~900px or less)
 */
export type TableVariant = 'default' | 'compact';

/**
 * Variant-aware column width configuration
 *
 * Allows specifying different widths for different table contexts.
 * The compact variant uses partial overrides - only specify properties
 * that differ from the default.
 *
 * @example
 * ```typescript
 * const variantWidth: VariantColumnWidth = {
 *   default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
 *   compact: { fixedWidth: 140 }, // Only override fixedWidth
 * };
 * ```
 */
export interface VariantColumnWidth {
  default: ColumnWidth;
  compact?: Partial<ColumnWidth>;
}

/**
 * Type guard to check if width configuration has variant support
 */
function hasVariants(
  width: ColumnWidth | VariantColumnWidth
): width is VariantColumnWidth {
  return 'default' in width;
}

/**
 * Column configuration entry
 *
 * Contains all metadata for a single column including:
 * - Width configuration (supports variant-aware widths)
 * - Display label
 * - Behavior flags (sortable, filterable)
 * - Additional metadata
 */
export interface ColumnConfig {
  width: ColumnWidth | VariantColumnWidth;
  label?: string;
  sortable?: boolean;
  filterable?: boolean;
  info?: string;
}

/**
 * Entity-specific column configurations
 * Maps column keys to their configurations
 */
export interface EntityColumns {
  [columnKey: string]: ColumnConfig;
}

/**
 * Entity type literal union
 */
export type EntityType =
  | 'shared'
  | 'seed'
  | 'asset'
  | 'technology'
  | 'risk'
  | 'agent'
  | 'agentUser'
  | 'agentCredential'
  | 'customer'
  | 'job'
  | 'account'
  | 'access'
  | 'capability';

/**
 * Central column registry organized by entity type
 */
export interface ColumnRegistry {
  shared: EntityColumns;
  seed: EntityColumns;
  asset: EntityColumns;
  technology: EntityColumns;
  risk: EntityColumns;
  agent: EntityColumns;
  agentUser: EntityColumns;
  agentCredential: EntityColumns;
  customer: EntityColumns;
  job: EntityColumns;
  account: EntityColumns;
  access: EntityColumns;
  capability: EntityColumns;
}

// ============================================================================
// CENTRAL COLUMN REGISTRY
// ============================================================================

/**
 * Column Registry
 *
 * ALL column width configurations for ALL entity types.
 * Edit this file to change column widths across the platform.
 *
 * Width Strategy:
 * - Fixed columns: fixedWidth = minWidth = maxWidth (all equal)
 * - Expandable columns: minWidth ONLY (no fixedWidth, no maxWidth - allows flexGrow)
 * - Flexible columns: minWidth + maxWidth (no fixedWidth - constrained expansion)
 *
 * Source Files (Day 1 migration):
 * - Seeds: modules/chariot/ui/src/sections/seeds/config/columns.ts
 * - Assets: modules/chariot/ui/src/sections/asset/config/columns.ts
 * - Technologies: modules/chariot/ui/src/sections/technology/config/columns.ts
 */
export const COLUMN_REGISTRY: ColumnRegistry = {
  /**
   * ========================================================================
   * SHARED COLUMNS
   * ========================================================================
   *
   * Columns used by multiple entity types with consistent behavior.
   *
   * Email column is used by:
   * - Seeds (domain seed email field)
   * - Risks (future)
   *
   * Status column is used by:
   * - Seeds (Active, Frozen status)
   * - Assets (Active, Frozen status)
   *
   * Date columns (created, visited) are used by:
   * - Seeds (First Seen, Last Seen)
   * - Assets (First Seen, Last Seen)
   * - Technologies (First Seen, Last Seen)
   *
   * Tags column is used by:
   * - Assets (user-defined tags)
   * - Technologies (user-defined tags)
   * - Seeds (future)
   */
  shared: {
    // Email column (Seeds, Risks)
    // Source: Seeds SEED_COLUMN_WIDTHS_DOMAIN.email
    email: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Email',
      sortable: true,
      filterable: true,
      info: 'Email address',
    },

    // Status column (Seeds, Assets)
    // Source: Seeds SEED_COLUMN_WIDTHS_DOMAIN.status, Assets ASSET_COLUMN_WIDTHS.status
    // Updated: 2025-11-18 - Standardized to 100px (consistent with risk.status)
    status: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: 'Status',
      sortable: true,
      filterable: true,
      info: 'Entity status (Active, Frozen, etc.)',
    },

    // Created date - First Seen (Assets, Seeds, Technologies)
    // Updated: 2025-10-27 - Increased from 40px to 130px to fit full dates
    // Needs to display: "Apr 30, 2025" (13 chars) or "26 days ago" (11 chars)
    created: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'First Seen',
      sortable: true,
      info: 'Date entity was first discovered',
    },

    // Updated/Visited date - Last Seen (Assets, Technologies)
    // Updated: 2025-12-10 - Increased from 120px to 150px for Access Logs "Last Activity" column
    // Needs to display: "15 minutes ago" (14 chars) without wrapping
    visited: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Last Seen',
      sortable: true,
      info: 'Date entity was last observed',
    },

    // Tags column (Assets, Technologies)
    // Source: Assets ASSET_COLUMN_WIDTHS.tags, Technologies TECHNOLOGY_COLUMN_WIDTHS.tags
    // Note: Assets uses 80px base, Technologies uses 150px base - storing Assets width as default
    // Updated: 2025-10-27 - Increased from 40px to 100px for tag chips
    tags: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Tags',
      filterable: true,
      info: 'User-defined tags',
    },

    /**
     * ========================================================================
     * NETWORK COLUMNS (SHARED ACROSS DRAWERS)
     * ========================================================================
     *
     * Source: Drawer table migrations
     * Used by: Asset Drawer (Technologies tab), Technology Drawer (Assets Impacted tab)
     *
     * Migration: 2025-11-18
     * - Extracted from inline definitions in drawer tables
     * - Standardized widths based on data analysis
     */

    // Port column - Network port number
    // Source: TechnologiesTab.tsx (100px), TechnologyAssetsImpacted.tsx (100px)
    port: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Port',
      sortable: true,
      filterable: true,
      info: 'Network port number (1-65535)',
    },

    // Protocol column - Network protocol
    // Source: TechnologiesTab.tsx (120px)
    protocol: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Protocol',
      sortable: true,
      filterable: true,
      info: 'Network protocol (TCP, UDP, HTTP, HTTPS)',
    },

    // Service column - Network service name
    // Source: TechnologiesTab.tsx (120px), TechnologyAssetsImpacted.tsx (120px)
    service: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Service',
      sortable: true,
      filterable: true,
      info: 'Network service name',
    },

    // IP Address column - IPv4 or IPv6 address
    // Source: TechnologyAssetsImpacted.tsx (200px)
    ipAddress: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'IP Address',
      sortable: true,
      filterable: true,
      info: 'IPv4 or IPv6 address',
    },

    // Domain Name column - FQDN
    // Source: TechnologyAssetsImpacted.tsx (flexible)
    // Updated: 2025-11-18 - Added maxWidth: 400 to prevent horizontal scroll in drawers
    domainName: {
      width: { minWidth: 200, maxWidth: 400 }, // Bounded flexibility
      label: 'Domain Name',
      sortable: true,
      filterable: true,
      info: 'Fully qualified domain name (FQDN)',
    },
  },

  /**
   * ========================================================================
   * SEED-SPECIFIC COLUMNS
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/seeds/config/columns.ts
   *
   * Three seed types with different column configurations:
   * - Domain seeds: identifier, registrar, email, registrant, origin
   * - IP seeds: identifier, registrant, registrar, country, origin
   * - WebApp seeds: identifier, origin
   *
   * Common columns (status, email) are in shared section.
   */
  seed: {
    // Identifier column - Used by all seed types
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.identifier (200px), SEED_COLUMN_WIDTHS_WEBAPP.identifier (300px)
    // Note: Domain/IP use 200px, WebApp uses 300px - storing domain width as default
    identifier: {
      width: { fixedWidth: 180, minWidth: 180, maxWidth: 180 },
      label: 'Identifier',
      sortable: true,
      filterable: true,
      info: 'Seed identifier (domain, IP, URL)',
    },

    // Registrar column - Domain and IP seeds
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.registrar
    registrar: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Registrar',
      sortable: true,
      filterable: true,
      info: 'Domain registrar',
    },

    // Registrant column - Domain and IP seeds
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.registrant
    registrant: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Registrant',
      sortable: true,
      filterable: true,
      info: 'Domain registrant',
    },

    // Origin column - All seed types
    // Source: SEED_COLUMN_WIDTHS_DOMAIN.origin
    // Updated: 2025-10-27 - Increased from 50px to 100px for origin badges
    origin: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Added By',
      sortable: true,
      filterable: true,
      info: 'Seed origin source',
    },

    // Country column - IP seeds only
    // Source: SEED_COLUMN_WIDTHS_IP.country
    country: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Country',
      sortable: true,
      filterable: true,
      info: 'Country code (ISO 3166-1 alpha-2)',
    },
  },

  /**
   * ========================================================================
   * ASSET-SPECIFIC COLUMNS
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/asset/config/columns.ts
   *
   * Asset table columns for domains, IPs, web applications, and network services.
   * Common columns (status, tags, created, visited) are in shared section.
   */
  asset: {
    // Identifier column - Main asset identifier
    // Source: ASSET_COLUMN_WIDTHS.identifier
    identifier: {
      width: { fixedWidth: 200, minWidth: 200 }, //expandable column, no maxWidth
      label: 'Asset',
      sortable: true,
      filterable: true,
      info: 'Asset identifier (domain, IP, URL)',
    },

    // ASN Name column
    // Source: ASSET_COLUMN_WIDTHS.asname
    asname: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'ASN Name',
      sortable: true,
      filterable: true,
      info: 'Autonomous System Name',
    },

    // ASN Number column
    // Source: ASSET_COLUMN_WIDTHS.asnumber
    asnumber: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: 'ASN',
      sortable: true,
      filterable: true,
      info: 'Autonomous System Number',
    },

    // Country column
    // Source: ASSET_COLUMN_WIDTHS.country
    country: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Country',
      sortable: true,
      filterable: true,
      info: 'Country code (ISO 3166-1 alpha-2)',
    },

    // IP Version column
    // Source: ASSET_COLUMN_WIDTHS.ipVersion
    ipVersion: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'IP Ver',
      sortable: false,
      filterable: false,
      info: 'IP version (IPv4 or IPv6)',
    },

    // Attack Surface column
    // Source: ASSET_COLUMN_WIDTHS.attackSurface
    attackSurface: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Attack Surface',
      sortable: true,
      filterable: true,
      info: 'Attack surface classification',
    },

    // Origins column
    // Source: ASSET_COLUMN_WIDTHS.origins
    origins: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Origins',
      sortable: true,
      filterable: true,
      info: 'Discovery sources for this asset',
    },

    // Port column
    // Source: ASSET_COLUMN_WIDTHS.port
    port: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Port',
      sortable: true,
      filterable: true,
      info: 'Network port number',
    },

    // Protocol column
    // Source: ASSET_COLUMN_WIDTHS.protocol
    protocol: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Proto',
      sortable: true,
      filterable: true,
      info: 'Network protocol',
    },
  },

  /**
   * ========================================================================
   * TECHNOLOGY-SPECIFIC COLUMNS
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/technology/config/columns.ts
   *
   * Technology table columns for displaying detected technologies.
   * Common columns (tags, created, visited) are in shared section.
   */
  technology: {
    // Vendor column
    // Source: TECHNOLOGY_COLUMN_WIDTHS.vendor
    // Updated: 2025-11-18 - Increased from 60px to 70px for better vendor name display
    // Updated: 2026-01-08 - Increased from 100px to 120px to fit full vendor names (e.g., "paloaltonetworks")
    vendor: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Vendor',
      sortable: true,
      filterable: true,
      info: 'Technology vendor/manufacturer',
    },

    // Name column - Main technology name
    // Source: TECHNOLOGY_COLUMN_WIDTHS.name
    name: {
      width: { minWidth: 300 },
      label: 'Technology',
      sortable: true,
      filterable: true,
      info: 'Technology product name',
    },

    // Product column - Technology product name
    // Source: TechnologiesTab.tsx (130px)
    product: {
      width: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      label: 'Product',
      sortable: true,
      filterable: true,
      info: 'Technology product name',
    },

    // Version column - Technology version number
    // Source: TechnologiesTab.tsx (100px)
    version: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Version',
      sortable: true,
      filterable: true,
      info: 'Technology version number',
    },

    // CPE String column - Common Platform Enumeration
    // Source: TechnologiesTab.tsx (flexible)
    cpeString: {
      width: { minWidth: 250 }, // Flexible for long CPE strings
      label: 'CPE String',
      sortable: false,
      filterable: false,
      info: 'Common Platform Enumeration identifier',
    },
  },

  /**
   * ========================================================================
   * RISK-SPECIFIC COLUMNS (VULNERABILITIES)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/vulnerabilities/config/columns.ts
   *
   * Vulnerability/Risk table columns for security risk management.
   * Note: Backend uses "Risk" entity type, but UI refers to these as "Vulnerabilities"
   *
   * Common columns (created, visited) are in shared section.
   *
   * Migration: Day 5 - 2025-10-23
   * - Migrated from COLUMN_WIDTHS in vulnerabilities/config/columns.ts
   * - Standardized date columns (created/visited) to match Assets/Technologies (60px)
   * - Previous values: created=180px, visited=180px
   */
  risk: {
    // Status column - Vulnerability lifecycle state
    // Source: COLUMN_WIDTHS.status (150px)
    // Updated: 2025-11-18 - Reduced from 110px to 100px for drawer space optimization
    // Updated: 2025-12-29 - Added compact variant for drawer context (+35px for "DEMONSTRATED" text)
    // Updated: 2026-03-06 - Increased default from 95px to 120px to prevent "DEMONSTRATED" truncation (ENG-1919)
    status: {
      width: {
        default: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
        compact: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      },
      label: 'Status',
      sortable: true,
      info: 'Vulnerability status (Opened, Triaged, Remediated, Accepted, Deleted)',
    },

    // Priority/Severity column
    // Source: COLUMN_WIDTHS.priority (120px)
    // Updated: 2025-10-27 - Increased from 40px to 100px for severity badges
    // Updated: 2025-12-29 - Added compact variant for drawer context (+10px for better badge display)
    priority: {
      width: {
        default: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
        compact: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      },
      label: 'Severity',
      sortable: false,
      info: 'Vulnerability severity level (Critical, High, Medium, Low, Info, Exposure)',
    },

    // Severity column - Alias for priority (used in query builder filter names)
    // VulnerabilityEntityConfig maps 'severity' → 'priority' field
    severity: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Severity',
      sortable: false,
      info: 'Vulnerability severity level (Critical, High, Medium, Low, Info, Exposure)',
    },

    // Name column - Main vulnerability identifier
    // Source: COLUMN_WIDTHS.name (300px)
    // Updated: 2025-12-29 - Added compact variant for drawer context (flexible width 250-350px)
    name: {
      width: {
        default: { fixedWidth: 400, minWidth: 400, maxWidth: 400 },
        compact: { fixedWidth: undefined, minWidth: 250, maxWidth: 350 }, // Flexible in drawer - explicitly unset fixedWidth
      },
      label: 'Vulnerability',
      sortable: true,
      filterable: false,
      info: 'Vulnerability name/title',
    },

    // CVSS Score column
    // Source: COLUMN_WIDTHS.cvss (100px)
    cvss: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'CVSS',
      sortable: false,
      info: 'Common Vulnerability Scoring System score',
    },

    // EPSS Score column
    // Source: COLUMN_WIDTHS.epss (100px)
    epss: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'EPSS',
      sortable: false,
      info: 'Exploit Prediction Scoring System probability',
    },

    // KEV (Known Exploited Vulnerability) column
    // Source: COLUMN_WIDTHS.kev (100px)
    kev: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'CISA KEV',
      sortable: false,
      info: 'CISA Known Exploited Vulnerabilities catalog status',
    },

    // Exploit availability column
    // Source: COLUMN_WIDTHS.exploit (140px)
    exploit: {
      width: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      label: 'Public Exploits',
      sortable: false,
      info: 'Public exploit availability indicator',
    },

    // ITSM Ticket column
    // Added: 2026-02-23 - ITSM ticket number for vulnerability tracking (ENG-390)
    ticket: {
      width: { fixedWidth: 160, minWidth: 160, maxWidth: 160 },
      label: 'Ticket',
      sortable: false,
      filterable: false,
      info: 'ITSM ticket linked to this vulnerability',
    },

    // Origin column
    // Source: COLUMN_WIDTHS.origin (140px)
    // Updated: 2025-10-27 - Increased from 50px to 100px for origin badges
    // Updated: 2025-12-29 - Added compact variant for drawer context (hidden via 0px width)
    origin: {
      width: {
        default: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
        compact: { fixedWidth: 0, minWidth: 0, maxWidth: 0 }, // Hide in drawer
      },
      label: 'Origin',
      sortable: false,
      filterable: true,
      info: 'Vulnerability detection source (scanner, integration, manual)',
    },

    // Last Seen column - Override shared.visited with risk-specific variant widths
    // Source: shared.visited (80px default)
    // Updated: 2025-12-29 - Added compact variant for drawer context (130px for dates like "15 minutes ago")
    visited: {
      width: {
        default: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
        compact: { fixedWidth: 130, minWidth: 130, maxWidth: 130 },
      },
      label: 'Last Seen',
      sortable: true,
      info: 'Date vulnerability was last observed',
    },

    // CVE column - CVE identifier for the vulnerability
    // Added: 2025-12-05 - Added to support CVE filter in query builder
    cve: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'CVE',
      sortable: true,
      filterable: true,
      info: 'Common Vulnerabilities and Exposures identifier (e.g., CVE-2025-55182)',
    },

    // GUID column - Unique vulnerability instance identifier (UUID v4)
    guid: {
      width: {
        default: { fixedWidth: 85, minWidth: 85, maxWidth: 85 },
      },
      label: 'ID',
      sortable: false,
      info: 'Unique identifier for this vulnerability instance (UUID v4)',
    },

    // Note: created and visited columns use shared date columns
    // Previous local values: created=180px, visited=180px
    // New standardized values: 60px (matches Assets/Technologies)
  },

  /**
   * ========================================================================
   * AGENT-SPECIFIC COLUMNS (AEGIS AGENTS)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/agents/config/columnDefinitions.tsx
   *
   * Aegis security agent table columns for agent management and monitoring.
   * Agents are sourced from Velociraptor backend (not Neo4j/DynamoDB).
   *
   * Common columns (last_seen_at uses shared 'visited' pattern) stored in shared section.
   *
   * Migration: Day 6 - 2025-10-23
   * - Migrated from local columnDefinitions.tsx to centralized registry
   * - Maintains agent-specific data sources while centralizing cosmetics
   * - Previous values: status=150px, client_id=250px, last_seen_at=60px (CELL_WIDTHS.date)
   */
  agent: {
    // Status column - Agent online/offline/warning/error state
    // Source: columnDefinitions.tsx status (150px)
    // Note: Uses AgentStatusBadge component for rendering
    // Updated: 2025-12-06 - Standardized to 70px to match shared status column
    status: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: 'Status',
      sortable: true,
      info: 'Agent connection status (Online, Offline, Warning, Error)',
    },

    // Client ID column - Unique agent identifier
    // Source: columnDefinitions.tsx client_id (250px)
    // Note: Shows Cloudflare tunnel icon when configured
    client_id: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Agent',
      sortable: true,
      info: 'Unique agent identifier (Client ID)',
    },

    // FQDN/Asset column - Fully qualified domain name or hostname
    // Source: columnDefinitions.tsx fqdn (expandable, no fixed width)
    // Note: Displays FQDN with IP address dropdown
    fqdn: {
      width: { minWidth: 160 },
      label: 'Asset',
      sortable: true,
      info: 'Agent fully qualified domain name (FQDN) or hostname',
    },

    // Last Seen column - Last agent check-in timestamp
    // Source: CELL_WIDTHS.date (60px) - matches Assets/Technologies/Risks
    // Note: Uses shared 'visited' pattern for consistency
    // Updated: 2025-12-06 - Standardized to 80px to match shared date columns (created/visited)
    last_seen_at: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Last Seen',
      sortable: true,
      info: 'Last time agent checked in with server',
    },
  },

  /**
   * ========================================================================
   * AGENT USER COLUMNS (AEGIS AGENT USER MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/agents/components/aegis-management/user/UserManagementTab.tsx
   *
   * Linux user management columns for Aegis agents.
   * Used in AegisManagementModal Users tab.
   *
   * Migration: 2025-12-06
   * - Migrated from inline column definitions to centralized registry
   * - Simplified table from Table to TanStackTable component
   */
  agentUser: {
    // Username column - Linux system username
    // Updated: 2025-12-07 - Changed from expandable to fixed to prevent huge white space
    username: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'User',
      sortable: true,
      info: 'Linux system username (first.last format)',
    },

    // Status column - User account status
    // Updated: 2025-12-07 - Reduced from 100px to 90px (sufficient for ACTIVE badge)
    status: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Status',
      sortable: false,
      info: 'User account status (always Active)',
    },

    // Actions column - Delete action
    // Updated: 2025-12-07 - Increased from 60px to 70px for better right padding
    actions: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: '',
      sortable: false,
    },
  },

  /**
   * ========================================================================
   * AGENT CREDENTIAL COLUMNS (AEGIS AGENT CREDENTIALS MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/agents/components/aegis-management/credentials/CredentialsManagementTab.tsx
   *
   * Active Directory credential management columns for Aegis agents.
   * Used in AegisManagementModal Credentials tab.
   *
   * Migration: 2025-12-07
   * - Migrated from inline list view to TanStackTable pattern
   * - Follows UserManagementTab pattern for consistency
   */
  agentCredential: {
    // Name column - Credential label
    name: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Credential',
      sortable: false,
      info: 'Credential label or name',
    },

    // Credential ID column - Unique identifier
    credentialId: {
      width: { fixedWidth: 280, minWidth: 280, maxWidth: 280 },
      label: 'ID',
      sortable: false,
      info: 'Unique credential identifier',
    },

    // Created column - When credential was added
    created: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Created',
      sortable: true,
      info: 'When credential was added',
    },

    // Actions column - Delete action
    actions: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: '',
      sortable: false,
    },
  },

  /**
   * ========================================================================
   * CUSTOMER-SPECIFIC COLUMNS (CUSTOMER MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/customerManagement/config/columns.ts
   *
   * Customer management table columns for Praetorian staff to manage all customer tenants.
   * Displays subscription status, customer type, risk metrics, and operational health.
   *
   * Migration: Day 7 - 2025-10-24
   * - Migrated from CUSTOMER_COLUMN_WIDTHS in customerManagement/config/columns.ts
   * - Note: 'high' column had inconsistent widths (fixedWidth: 60, minWidth: 80, maxWidth: 80)
   *   Using fixedWidth: 60 as source of truth (likely typo in original config)
   */
  customer: {
    // View column - Action button to view customer account
    view: {
      width: { fixedWidth: 24, minWidth: 24, maxWidth: 25 },
      label: 'View',
      sortable: false,
      info: 'Open customer account details',
    },

    // Status column - Customer lifecycle status
    // Updated: 2025-10-27 - Increased from 40px to 110px for status badges
    status: {
      width: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
      label: 'Status',
      sortable: true,
      info: 'Customer lifecycle status (Active, Paused, Setup, Upcoming, Completed)',
    },

    // Display Name column - Primary customer identifier (expandable)
    displayName: {
      width: { fixedWidth: 300, minWidth: 200 },
      label: 'Customer',
      sortable: true,
      info: 'Customer display name with logo',
    },

    // Customer Type column
    type: {
      width: { fixedWidth: 70, minWidth: 70, maxWidth: 70 },
      label: 'Type',
      sortable: true,
      info: 'Customer type classification',
    },

    // Integration Health column
    health: {
      width: { fixedWidth: 40, minWidth: 40, maxWidth: 40 },
      label: 'Health',
      sortable: true,
      info: 'Integration health (healthy / total)',
    },

    // Last Compromise Date column
    lastCompromiseDate: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Last Compromise',
      sortable: true,
      info: 'Date of most recent critical demonstrated risk',
    },

    // Critical Risk Count column
    critical: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Critical',
      sortable: true,
      info: 'Critical severity risk count',
    },

    // High Risk Count column (FIXED: was fixedWidth: 60, minWidth: 80, maxWidth: 80)
    high: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'High',
      sortable: true,
      info: 'High severity risk count',
    },

    // Medium Risk Count column
    medium: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Medium',
      sortable: true,
      info: 'Medium severity risk count',
    },

    // Low Risk Count column
    low: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Low',
      sortable: true,
      info: 'Low severity risk count',
    },

    // Info Risk Count column
    info: {
      width: { fixedWidth: 50, minWidth: 50, maxWidth: 50 },
      label: 'Info',
      sortable: true,
      info: 'Info severity risk count',
    },
  },

  /**
   * ========================================================================
   * JOB-SPECIFIC COLUMNS (ASYNC JOB EXECUTION)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/jobs/config/columns.ts
   *
   * Job execution tracking columns for security scan job management.
   * Jobs represent async security tool executions with real-time status tracking.
   *
   * Note: Status column uses platform-standard width (110px) for consistency
   * with other entity status badges. Updated column uses shared.visited pattern
   * (130px) for standardized date formatting.
   *
   * Migration: 2025-11-15
   * - Migrated from COLUMN_WIDTHS in jobs/config/columns.ts
   * - Standardized status column from 100px → 110px (platform standard)
   * - Standardized updated column from flexible → 130px (shared.visited pattern)
   * - Added proper column IDs for sourceTarget and actions
   * - Enabled sorting for updated column
   * - Previous values: status=100px, sourceTarget=flexible, updated=flexible, actions=120px
   */
  job: {
    // Status column - Job execution state
    // Source: jobs/config/columns.ts status (100px)
    // Updated: Increased from 40px → 60px for better header text display
    status: {
      width: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
      label: 'Status',
      sortable: true, // Job status changes in real-time via polling
      info: 'Job execution status (Running, Queued, Pass, Fail)',
    },

    // Job Type column - Displays the type of security scan being executed
    // Migration: 2025-11-15 - Split from sourceTarget column
    // Updated: 2025-12-07 - Increased from 130px → 200px for long job names
    // Examples: "LINUX-AD-CERTIFY#2025-12-05T16:18:58Z" (43 chars)
    type: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Job',
      sortable: true,
      info: 'Type of security scan job (port-scanning, nuclei, ad-certify, etc.)',
    },

    // Source column - Displays the source entity being scanned
    // Migration: 2025-11-15 - Split from sourceTarget column
    // Fixed width for stable layout
    source: {
      width: { fixedWidth: 90, minWidth: 90, maxWidth: 90 },
      label: 'Source',
      sortable: false,
      info: 'Source asset or entity being scanned',
    },

    // Target column - Displays the target DNS or IP being scanned
    // Migration: 2025-11-15 - Split from sourceTarget column
    // Expandable to accommodate long DNS names
    target: {
      width: { minWidth: 400 },
      label: 'Target',
      sortable: false,
      info: 'Target DNS or IP address being scanned',
    },

    // Created column - Job creation timestamp
    // Added: 2025-11-16 - Following platform pattern (Asset, Vulnerability, Seed all show created + updated)
    // Uses shared date column width (130px) for consistency with other tables
    created: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Job Created Time',
      sortable: true,
      info: 'Timestamp when job was created and queued in the system',
    },

    // Updated column - Last status change timestamp
    // Source: jobs/config/columns.ts updated (flexible)
    // Updated: Changed from flexible → 130px fixed (matches shared.visited pattern)
    // Note: For completed jobs this represents completion time, for running jobs when they started
    // Enable sorting to allow users to see most recent jobs first
    updated: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Job Completion Time',
      sortable: true, // Enable sorting by recency
      info: 'Last status change timestamp (completion time for finished jobs)',
    },
  },

  /**
   * ========================================================================
   * ACCOUNT-SPECIFIC COLUMNS (USER MANAGEMENT)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/components/AuthorizedUsersTables.tsx
   *
   * User management table columns for displaying authorized users.
   * Used in settings and customer management interfaces.
   *
   * Variant Support (2025-12-02):
   * - default: Full page context (Users page) - 900px+ available width
   * - compact: Drawer context (Customer Management) - ~900px constrained width
   *
   * Compact Column Layout (Role hidden):
   * - displayName: 140px (truncates with OverflowText)
   * - authMethod: 80px (shortened label "Auth Method")
   * - created: 110px (date format fits comfortably)
   * - lastActivity: 110px (date format fits comfortably)
   * - actions: 60px (two icon buttons)
   * - Total: 500px (400px buffer for drawer padding/margins)
   *
   * Migration: 2025-12-02
   */
  account: {
    // Display Name column - User identifier (email or display name)
    displayName: {
      width: {
        default: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
        compact: { fixedWidth: 140, minWidth: 140, maxWidth: 140 },
      },
      label: 'User',
      sortable: true,
      info: 'User display name or email address',
    },

    // Role column - User permission level
    // Note: Hidden in compact variant via column visibility logic in AuthorizedUsersTables.tsx
    role: {
      width: {
        default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
        compact: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      },
      label: 'Role',
      sortable: true,
      info: 'User role (Account owner, Collaborator)',
    },

    // Authentication Method column - SSO vs local auth
    authMethod: {
      width: {
        default: { fixedWidth: 250, minWidth: 250, maxWidth: 250 },
        compact: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      },
      label: 'Authentication Method',
      sortable: false,
      info: 'Single Sign-On or local authentication',
    },

    // Created column - Account creation timestamp
    created: {
      width: {
        default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
        compact: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      },
      label: 'Account Creation',
      sortable: true,
      info: 'Date user account was created',
    },

    // Last Activity column - Last access timestamp
    lastActivity: {
      width: {
        default: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
        compact: { fixedWidth: 110, minWidth: 110, maxWidth: 110 },
      },
      label: 'Last Activity',
      sortable: true,
      info: 'Last time user accessed the account',
    },

    // Actions column - Management actions
    actions: {
      width: {
        default: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
        compact: { fixedWidth: 60, minWidth: 60, maxWidth: 60 },
      },
      label: 'Actions',
      sortable: false,
      info: 'User management actions (revoke, reinvite)',
    },
  },

  /**
   * ========================================================================
   * ACCESS-SPECIFIC COLUMNS (ACCESS LOGS)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/UsersPage.tsx
   *
   * Access log table columns for displaying user access history.
   * Used in Users page to track SSO and username/password logins.
   *
   * Migration: 2025-12-02
   */
  access: {
    // Email column - User identifier (expandable to fill remaining table width)
    // Updated: 2025-12-10 - Increased to 350px to fit full email addresses
    // Updated: 2026-03-13 - Changed to expandable (removed maxWidth) to fill container width
    email: {
      width: { minWidth: 200 },
      label: 'User',
      sortable: true,
      info: 'User email address',
    },

    // Access Type column - SSO or Username/Password
    // Updated: 2025-12-10 - Increased to 200px to help fill table width
    type: {
      width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
      label: 'Access Type',
      sortable: true,
      info: 'Authentication method (SSO or Username/Password)',
    },

    // Role column - User's RBAC role
    role: {
      width: { fixedWidth: 150, minWidth: 150, maxWidth: 150 },
      label: 'Role',
      sortable: true,
      info: 'User role in the organization',
    },

    // SSO Domain column - Domain for SSO users
    // Updated: 2026-03-13 - Changed to expandable (no maxWidth) to accommodate long SSO domains
    value: {
      width: { minWidth: 180 },
      label: 'SSO Domain',
      sortable: false,
      info: 'SSO domain (only for SSO users)',
    },

    // Note: lastActivity uses shared.visited pattern
    // Updated timestamp - Last access time (uses shared 'visited' column)
  },

  /**
   * ========================================================================
   * CAPABILITY-SPECIFIC COLUMNS (SECURITY SCANNING CAPABILITIES)
   * ========================================================================
   *
   * Source: modules/chariot/ui/src/sections/attacks/capabilities/Capabilities.tsx
   *
   * Capability table columns for displaying security scanning capabilities.
   * Capabilities represent scanning modules that can be executed against assets.
   *
   * Migration: 2025-12-15
   * - Migrated from inline column definitions to centralized registry
   * - Follows established column registry patterns
   */
  capability: {
    // Title/Name column - Capability display name
    title: {
      width: { fixedWidth: 180, minWidth: 180, maxWidth: 180 },
      label: 'Name',
      sortable: true,
      info: 'Capability name and version',
    },

    // Description column - Capability description (expandable)
    description: {
      width: { minWidth: 200 }, // Expandable column, no maxWidth
      label: 'Description',
      sortable: false,
      info: 'Capability description and purpose',
    },

    // Surface column - Execution surface (external, internal, cloud, scm)
    surface: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Surface',
      sortable: true,
      filterable: true,
      info: 'Execution surface (External, Internal, Cloud, SCM)',
    },

    // Target column - Resource type the capability operates on
    target: {
      width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
      label: 'Resource',
      sortable: true,
      filterable: true,
      info: 'Target resource type (Domain, IP, WebApp, AD Domain)',
    },

    // Category column - Capability category classification
    category: {
      width: { fixedWidth: 120, minWidth: 120, maxWidth: 120 },
      label: 'Category',
      sortable: true,
      filterable: true,
      info: 'Capability category (Discovery, Enumeration, Exploitation)',
    },

    // Platform column - Platform requirement for capability execution
    runs_on: {
      width: { fixedWidth: 80, minWidth: 80, maxWidth: 80 },
      label: 'Platform',
      sortable: true,
      filterable: true,
      info: 'Platform requirement (Linux, Windows, macOS)',
    },
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Helper function to get column width configuration with variant support
 *
 * Lookup strategy:
 * 1. Check entity-specific columns first (shared, seed, asset, technology, risk, etc.)
 * 2. If not found in entity-specific, fall back to shared columns (for backward compatibility)
 * 3. Return default width if not found (with console warning)
 *
 * Variant handling:
 * - If column has variant-aware widths, returns the appropriate variant
 * - Compact variant merges overrides with default base values
 * - Legacy columns (no variants) return their width directly
 *
 * @param entityType - Entity type (shared, seed, asset, technology, risk, agent, customer, job, account, access)
 * @param columnKey - Column key (email, status, identifier, port, protocol, etc.)
 * @param variant - Table variant ('default' or 'compact'), defaults to 'default'
 * @returns Column width configuration for the specified variant
 *
 * @example
 * ```typescript
 * // Get default variant width
 * const width = getColumnWidth('account', 'displayName');
 * // { fixedWidth: 150, minWidth: 150, maxWidth: 150 }
 *
 * // Get compact variant width
 * const compactWidth = getColumnWidth('account', 'displayName', 'compact');
 * // { fixedWidth: 120, minWidth: 120, maxWidth: 120 }
 *
 * // Legacy column (no variants) - returns same width regardless of variant
 * const legacyWidth = getColumnWidth('seed', 'email', 'compact');
 * // { fixedWidth: 200, minWidth: 200, maxWidth: 200 }
 * ```
 */
export function getColumnWidth(
  entityType: EntityType,
  columnKey: string,
  variant: TableVariant = 'default'
): ColumnWidth {
  // Try entity-specific first
  const entityConfig = COLUMN_REGISTRY[entityType]?.[columnKey];
  const sharedConfig = COLUMN_REGISTRY.shared[columnKey];

  const config = entityConfig || sharedConfig;

  if (!config) {
    // Default width if not found
    console.warn(
      `Column width not found for ${entityType}.${columnKey}, using default`
    );
    return { fixedWidth: 100, minWidth: 100, maxWidth: 100 };
  }

  const widthConfig = config.width;

  // Check if width has variant support
  if (hasVariants(widthConfig)) {
    if (variant === 'compact' && widthConfig.compact) {
      // Merge compact overrides with default base
      const merged: ColumnWidth = {
        ...widthConfig.default,
        ...widthConfig.compact,
      };

      // Validate fixed column invariant after merge
      if (merged.fixedWidth !== undefined) {
        if (
          merged.fixedWidth !== merged.minWidth ||
          merged.fixedWidth !== merged.maxWidth
        ) {
          console.warn(
            `[Table Registry] Invalid variant config for ${entityType}.${columnKey}: ` +
              `fixedWidth (${merged.fixedWidth}) must equal minWidth (${merged.minWidth}) and maxWidth (${merged.maxWidth}). ` +
              `Auto-correcting to use fixedWidth value.`
          );
          return {
            fixedWidth: merged.fixedWidth,
            minWidth: merged.fixedWidth,
            maxWidth: merged.fixedWidth,
          };
        }
      }

      return merged;
    }
    // Return default variant
    return widthConfig.default;
  }

  // Legacy: width is a plain ColumnWidth object (no variants)
  return widthConfig;
}

/**
 * Helper function to get full column configuration
 *
 * Returns complete ColumnConfig including width, label, sortable, etc.
 *
 * @param entityType - Entity type
 * @param columnKey - Column key
 * @returns Full column configuration or default
 *
 * @example
 * ```typescript
 * const emailConfig = getColumnConfig('seed', 'email');
 * // {
 * //   width: { fixedWidth: 200, minWidth: 200, maxWidth: 200 },
 * //   label: 'Email',
 * //   sortable: true,
 * //   filterable: true,
 * //   info: 'Email address'
 * // }
 * ```
 */
export function getColumnConfig(
  entityType: EntityType,
  columnKey: string
): ColumnConfig {
  const entityConfig = COLUMN_REGISTRY[entityType]?.[columnKey];
  if (entityConfig) {
    return entityConfig;
  }

  const sharedConfig = COLUMN_REGISTRY.shared[columnKey];
  if (sharedConfig) {
    return sharedConfig;
  }

  return {
    width: { fixedWidth: 100, minWidth: 100, maxWidth: 100 },
  };
}

/**
 * Get all column configurations for an entity type
 *
 * Merges shared columns with entity-specific columns.
 * Entity-specific columns override shared if there are naming conflicts.
 *
 * @param entityType - Entity type
 * @returns All column configurations including shared
 *
 * @example
 * ```typescript
 * const seedColumns = getEntityColumns('seed');
 * // Returns: { email, status, created, visited, identifier, registrar, registrant, origin, country }
 * ```
 */
export function getEntityColumns(entityType: EntityType): EntityColumns {
  return {
    ...COLUMN_REGISTRY.shared,
    ...COLUMN_REGISTRY[entityType],
  };
}

/**
 * Check if a column is sortable
 *
 * @param entityType - Entity type
 * @param columnKey - Column key
 * @returns Boolean indicating sortability
 *
 * @example
 * ```typescript
 * isColumnSortable('seed', 'email'); // true
 * isColumnSortable('asset', 'ipVersion'); // false
 * ```
 */
export function isColumnSortable(
  entityType: EntityType,
  columnKey: string
): boolean {
  const config = getColumnConfig(entityType, columnKey);
  return config?.sortable ?? false;
}

/**
 * Check if a column is filterable
 *
 * @param entityType - Entity type
 * @param columnKey - Column key
 * @returns Boolean indicating filterability
 *
 * @example
 * ```typescript
 * isColumnFilterable('seed', 'email'); // true
 * isColumnFilterable('asset', 'ipVersion'); // false
 * ```
 */
export function isColumnFilterable(
  entityType: EntityType,
  columnKey: string
): boolean {
  const config = getColumnConfig(entityType, columnKey);
  return config?.filterable ?? false;
}
